import { getFeatureSetting } from "../../feature-settings";
import { SerializedComponent } from "../../components/SerializedApp";
import { ValueProducer } from "../../components/types";
import { compileFilters, setColumnInRowForEditComponent } from "../builder-utils";
import {
    ActionPropertyDescriptor,
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    PropertyTableGetter,
    SchemaInspector,
} from "../components";
import { PropertySection } from "../components/property-sections";
import { getColumnProperty } from "../components/property-utils";
import {
    ArrayContentDescription,
    ArrayScreenDescription,
    ArrayScreenFormat,
    ArrayTransform,
    ComponentDescription,
    getPrimitiveNonHiddenColumns,
    getScreenComponentsArray,
    InputOutputTables,
    LegacyPropertyDescription,
    makeInputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    ScreenDescriptionKind,
    TableGlideType,
} from "../description";
import { InlineListBuild, makeEmptyMessage } from "./array-content";
import { ArrayScreenHandlerBase } from "./array-screen";
import { rowIndexColumnName } from "../../../common/google-sheets";
import { LetBinding } from "../serialized-component";
import { getDefaultPrimitiveActionKinds } from "../components/primitive";
import { makeDefaultComponents } from "../pages";

export interface TinderArrayContentDescription extends ArrayContentDescription {
    readonly swipedTimestamp: PropertyDescription;

    readonly leftActions: LegacyPropertyDescription | undefined;

    readonly swipeCondition: readonly ArrayTransform[] | undefined;
}

export interface TinderArrayScreenDescription extends ArrayScreenDescription, TinderArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.Tinder;
}

export class TinderArrayScreenHandler extends ArrayScreenHandlerBase<
    TinderArrayContentDescription,
    TinderArrayScreenDescription
> {
    constructor() {
        super(ArrayScreenFormat.Tinder, "Swipe", "tinder");
    }

    public get isLegacy(): boolean {
        return !getFeatureSetting("tinderArrayScreen");
    }

    public get hasComponents(): boolean {
        return true;
    }

    public get supportsInlineList(): boolean {
        return false;
    }

    protected get canReverse(): boolean {
        return false;
    }

    public getNeedsOrder(): boolean {
        return false;
    }

    protected makeDetailScreenTitle(
        builder: ComponentBuilder,
        _desc: TinderArrayContentDescription,
        _table: TableGlideType,
        _ctx: SerializedComponent
    ): SerializedComponent {
        // FIXME: Support a detail screen title
        return builder.b.constant("Details");
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: TinderArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        _caption: SerializedComponent | undefined,
        arrayScreenRow: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const swipedTimestampColumn = getColumnProperty(desc.swipedTimestamp);

        let action: SerializedComponent | undefined;
        let isEnabled: SerializedComponent | undefined;
        let valueToModify: SerializedComponent | undefined;
        const lets: LetBinding[] = [];

        if (swipedTimestampColumn !== undefined) {
            const setColumnsResult = setColumnInRowForEditComponent(
                b,
                undefined,
                swipedTimestampColumn,
                b.getProperty(swipedTimestampColumn, b.getContext, table),
                b.constant(0),
                table,
                undefined,
                b.getContext,
                { withDiode: false }
            );
            if (setColumnsResult === undefined) return b.emptyUI;

            action = setColumnsResult.action;
            valueToModify = setColumnsResult.valueToModify;
            isEnabled = setColumnsResult.isEnabled;
            lets.push(...setColumnsResult.lets);
        } else {
            isEnabled = b.constant(false);
        }

        const components = getScreenComponentsArray(desc);
        const visibilityConditions = builder.makeVisibilityConditions(table, components, b.getContext);
        const contents = builder.makeComponents(
            uniqueID,
            components,
            makeInputOutputTables(table),
            undefined,
            b.getVariable("outputContext"),
            searchActiveFlag,
            undefined,
            visibilityConditions,
            []
        );

        if (action !== undefined) {
            lets.push(["setSwipedTimestamp", action]);
        }

        const onLeftSequence = [];
        if (action !== undefined) {
            onLeftSequence.push(b.getVariable("setSwipedTimestamp"));
        }
        const { leftActions } = this.getActions(desc, builder);
        const onLeft = this.makeActions(builder, desc, leftActions, table);
        if (onLeft !== undefined) {
            onLeftSequence.push(onLeft);
        }

        const onRightSequence = [];
        if (action !== undefined) {
            onRightSequence.push(b.getVariable("setSwipedTimestamp"));
        }
        const onRight = this.makeDefaultAction(builder, desc, table);
        if (onRight !== undefined) {
            onRightSequence.push(onRight);
        }

        isEnabled = compileFilters(builder, table, b.getContext, desc.swipeCondition ?? [], isEnabled);

        const keyColumnName = table.rowIDColumn ?? rowIndexColumnName;

        return b.let(
            lets,
            b.ui("Tinder", {
                list: b.map(
                    arrayData,
                    b.object({
                        data: b.getContext,
                        swipedTimestamp: valueToModify,
                        key: b.getProperty(keyColumnName, b.getContext, table),
                        isEnabled,
                    })
                ),
                contents: b.holdContainer(b.let([["outputContext", b.getContext]], b.column(contents.components))),
                onLeft: b.sequence(onLeftSequence),
                onRight: b.sequence(onRightSequence),
                emptyMessage: makeEmptyMessage(b, searchActiveFlag, inlineListBuild),
                rowToSet: arrayScreenRow,
            })
        );
    }

    public getBasicSearchProperties(_desc: TinderArrayContentDescription): readonly string[] {
        // FIXME: Get search properties from components.  Right now we can't
        // search Swipe screens, but maybe we will at some point.
        return [];
    }

    public getContentPropertyDescriptors<T extends TinderArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        array: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const descriptors: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                array
            ),
            {
                kind: PropertyKind.Column,
                property: { name: "swipedTimestamp" },
                label: "Save last swipe",
                required: true,
                editable: true,
                isEditedInApp: true,
                searchable: false,
                // This is so that we don't overwrite random columns when somebody switches to Swipe
                emptyByDefault: true,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["swiped"],
                preferredType: "date-time",
                section: PropertySection.Data,
            },
        ];

        if (getFeatureSetting("swipeCondition")) {
            descriptors.push({
                kind: PropertyKind.Transforms,
                property: { name: "swipeCondition" },
                // FIXME: We don't actually use this label
                label: "Swipe condition",
                getIndirectTable: getPropertyTable,
                section: PropertySection.SwipeCondition,
                addText: "Add Condition",
                description: "Only allow swiping when a condition is met.",
                allowContextTable: true,
                allowLHSUserProfileColumns: false,
                withContainingScreen: false,
            });
        }

        return descriptors;
    }

    public getActionDescriptors<T extends TinderArrayContentDescription>(
        _desc: T,
        getIndirectTable: PropertyTableGetter<T> | undefined,
        schema: SchemaInspector | undefined
    ): readonly ActionPropertyDescriptor<T>[] {
        const kinds = getDefaultPrimitiveActionKinds(schema, undefined);
        return [
            {
                kind: PropertyKind.Action,
                property: { name: "leftActions" },
                label: "Left action",
                required: false,
                kinds,
                defaultAction: false,
                getIndirectTable,
                section: PropertySection.LeftAction,
            },
            {
                kind: PropertyKind.Action,
                property: { name: "actions" },
                label: "Right action",
                required: false,
                kinds,
                defaultAction: true,
                getIndirectTable,
                section: PropertySection.RightAction,
            },
        ];
    }

    public defaultDescription(
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        components: readonly ComponentDescription[]
    ): TinderArrayScreenDescription | undefined {
        let desc = super.defaultDescription(tables, ccc, components);
        if (desc === undefined) return undefined;

        if ((desc.components?.length ?? 0) === 0) {
            desc = { ...desc, components: makeDefaultComponents(ccc, tables.input) };
        }

        return desc;
    }

    public adjustContentDescriptionAfterUpdate(
        desc: TinderArrayContentDescription,
        updates: Partial<TinderArrayContentDescription> | undefined,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): TinderArrayContentDescription {
        desc = super.adjustContentDescriptionAfterUpdate(desc, updates, tables, ccc);
        if ((desc.components?.length ?? 0) === 0 && updates?.components === undefined) {
            desc = { ...desc, components: makeDefaultComponents(ccc, tables.input) };
        }
        return desc;
    }
}
